import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import ShopLayout from '../../components/layout/ShopLayout';
import './Shop.css';

export default function Shop() {
  const { products, categories, loading } = useData();
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    category: 'all',
    priceRange: 'all',
    sort: 'featured',
    isBio: false,
    freeShipping: false
  });
  const [priceRange, setPriceRange] = useState([0, 10000]);
  const maxPrice = 10000; // Prix maximum fixe pour le slider

  useEffect(() => {
    const filtered = products
      .filter(product => {
        const matchesSearch = product.name.toLowerCase().includes(filters.search.toLowerCase()) ||
                            product.description.toLowerCase().includes(filters.search.toLowerCase());
        const matchesCategory = filters.category === 'all' || product.categoryId === filters.category;
        
        // Pour les produits avec variantes, vérifier si au moins une variante correspond au filtre de prix
        let matchesPrice = true;
        if (product.hasVariants && product.variants && product.variants.length > 0) {
          matchesPrice = product.variants.some(variant => 
            variant.price >= priceRange[0] && variant.price <= priceRange[1]
          );
        } else {
          matchesPrice = product.price >= priceRange[0] && product.price <= priceRange[1];
        }
        
        const matchesBio = !filters.isBio || product.isBio;
        const matchesShipping = !filters.freeShipping || product.freeShipping;
        
        return product.isActive && matchesSearch && matchesCategory && matchesPrice && matchesBio && matchesShipping;
      })
      .sort((a, b) => {
        // Pour les produits avec variantes, utiliser le prix minimum pour le tri
        const getPriceForSort = (product) => {
          if (product.hasVariants && product.variants && product.variants.length > 0) {
            return Math.min(...product.variants.map(v => v.price));
          }
          return product.price;
        };
        
        switch (filters.sort) {
          case 'price-low':
            return getPriceForSort(a) - getPriceForSort(b);
          case 'price-high':
            return getPriceForSort(b) - getPriceForSort(a);
          case 'newest':
            return new Date(b.createdAt) - new Date(a.createdAt);
          default:
            return 0;
        }
      });

    setFilteredProducts(filtered);
  }, [products, filters, priceRange]);

  // Fonction pour afficher le prix d'un produit (avec ou sans variantes)
  const displayProductPrice = (product) => {
    // Cas avec prix promotionnel
    if (product.hasPromotion) {
      if (product.hasVariants && product.variants && product.variants.length > 0) {
        // Trouver les prix min et max pour les variantes (prix normaux et promotionnels)
        const prices = product.variants.map(v => v.price);
        const promoPrices = product.variants
          .filter(v => v.promoPrice && v.promoPrice > 0)
          .map(v => v.promoPrice);
        
        const minPrice = Math.min(...prices);
        const maxPrice = Math.max(...prices);
        
        // S'il y a des prix promotionnels
        if (promoPrices.length > 0) {
          const minPromoPrice = Math.min(...promoPrices);
          const maxPromoPrice = Math.max(...promoPrices);
          
          return (
            <div className="price-container">
              <span className="original-price">
                {minPrice === maxPrice ? `${minPrice}` : `${minPrice} - ${maxPrice}`} DZD
              </span>
              <span className="promo-price">
                {minPromoPrice === maxPromoPrice ? `${minPromoPrice}` : `${minPromoPrice} - ${maxPromoPrice}`} DZD
              </span>
            </div>
          );
        }
      }
      
      // Produit simple avec promotion
      if (product.promoPrice && product.promoPrice > 0) {
        return (
          <div className="price-container">
            <span className="original-price">{product.price} DZD</span>
            <span className="promo-price">{product.promoPrice} DZD</span>
          </div>
        );
      }
    }
    
    // Cas sans promotion
    if (product.hasVariants && product.variants && product.variants.length > 0) {
      const prices = product.variants.map(v => v.price);
      const minPrice = Math.min(...prices);
      const maxPrice = Math.max(...prices);
      
      if (minPrice === maxPrice) {
        return `${minPrice} DZD`;
      }
      return `${minPrice} - ${maxPrice} DZD`;
    }
    return `${product.price} DZD`;
  };

  return (
    <ShopLayout>
      <div className="shop-container">
        {/* Bannière */}
        <div className="shop-banner">
          <h1>Nos Produits</h1>
          <p>Découvrez notre sélection de produits de qualité</p>
        </div>

        <div className="shop-content">
          {/* Filtres latéraux */}
          <aside className="filters-sidebar">
            <div className="filter-section">
              <h3>Recherche</h3>
              <div className="search-box">
                <i className="fas fa-search"></i>
                <input
                  type="text"
                  placeholder="Rechercher un produit..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                />
              </div>
            </div>

            <div className="filter-section">
              <h3>Catégories</h3>
              <select
                value={filters.category}
                onChange={(e) => setFilters({ ...filters, category: e.target.value })}
              >
                <option value="all">Toutes les catégories</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-section">
              <h3>Prix</h3>
              <div className="price-slider">
                <div className="price-inputs">
                  <div className="price-input">
                    <span>Min</span>
                    <input
                      type="number"
                      value={priceRange[0]}
                      onChange={(e) => setPriceRange([
                        Math.min(parseInt(e.target.value) || 0, priceRange[1]),
                        priceRange[1]
                      ])}
                    />
                  </div>
                  <div className="price-input">
                    <span>Max</span>
                    <input
                      type="number"
                      value={priceRange[1]}
                      onChange={(e) => setPriceRange([
                        priceRange[0],
                        Math.max(parseInt(e.target.value) || 0, priceRange[0])
                      ])}
                    />
                  </div>
                </div>
                <div className="slider-container">
                  <input
                    type="range"
                    min="0"
                    max={maxPrice}
                    value={priceRange[0]}
                    onChange={(e) => setPriceRange([parseInt(e.target.value), priceRange[1]])}
                    className="price-slider-input"
                  />
                  <input
                    type="range"
                    min="0"
                    max={maxPrice}
                    value={priceRange[1]}
                    onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value)])}
                    className="price-slider-input"
                  />
                  <div 
                    className="slider-track" 
                    style={{
                      background: `linear-gradient(
                        to right,
                        #e9ecef ${(priceRange[0] / maxPrice) * 100}%,
                        var(--color-primary) ${(priceRange[0] / maxPrice) * 100}%,
                        var(--color-primary) ${(priceRange[1] / maxPrice) * 100}%,
                        #e9ecef ${(priceRange[1] / maxPrice) * 100}%
                      )`
                    }}
                  ></div>
                </div>
              </div>
            </div>

            <div className="filter-section">
              <h3>Trier par</h3>
              <select
                value={filters.sort}
                onChange={(e) => setFilters({ ...filters, sort: e.target.value })}
              >
                <option value="featured">En vedette</option>
                <option value="newest">Plus récents</option>
                <option value="price-low">Prix croissant</option>
                <option value="price-high">Prix décroissant</option>
              </select>
            </div>

            <div className="filter-section checkboxes">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={filters.isBio}
                  onChange={(e) => setFilters({ ...filters, isBio: e.target.checked })}
                />
                Produits Bio
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={filters.freeShipping}
                  onChange={(e) => setFilters({ ...filters, freeShipping: e.target.checked })}
                />
                Livraison gratuite
              </label>
            </div>
          </aside>

          {/* Grille de produits */}
          <main className="products-main">
            <div className="products-header">
              <p>{filteredProducts.length} produits trouvés</p>
            </div>

            {loading ? (
              <div className="loading-container">
                <div className="loading-content">
                  <i className="fas fa-spinner fa-spin"></i>
                  <p>Chargement des produits...</p>
                </div>
              </div>
            ) : (
              <div className="products-grid">
                {filteredProducts.map(product => (
                  <Link 
                    to={`/product/${product.id}`} 
                    key={product.id} 
                    className="product-card"
                  >
                    <div className="product-image">
                      {product.images?.[0] ? (
                        <img 
                          src={product.images[0].url} 
                          alt={product.name}
                          loading="lazy"
                        />
                      ) : (
                        <div className="no-image">
                          <i className="fas fa-image"></i>
                        </div>
                      )}
                      <div className="product-badges">
                        {product.isBio && (
                          <span className="badge bio">
                            <i className="fas fa-leaf"></i> BIO
                          </span>
                        )}
                        {product.freeShipping && (
                          <span className="badge shipping">
                            <i className="fas fa-truck"></i> Gratuit
                          </span>
                        )}
                        {product.hasVariants && (
                          <span className="badge variants">
                            <i className="fas fa-tags"></i> Options
                          </span>
                        )}
                        {product.hasPromotion && (
                          <span className="badge promo">
                            <i className="fas fa-percentage"></i> Promo
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="product-info">
                      <div className="product-category">
                        {categories.find(c => c.id === product.categoryId)?.name}
                      </div>
                      <h3>{product.name}</h3>
                      <p className="product-description">{product.description}</p>
                      <div className="product-footer">
                        <div className="price">
                          {product.hasPromotion && product.promoPrice && product.promoPrice > 0 ? (
                            <div className="price-container">
                              <span className="original-price">{product.price} DZD</span>
                              <span className="promo-price">{product.promoPrice} DZD</span>
                            </div>
                          ) : product.hasVariants && product.variants && product.variants.length > 0 ? (
                            displayProductPrice(product)
                          ) : (
                            `${product.price} DZD`
                          )}
                        </div>
                        {product.hasVariants && product.variants && product.variants.length > 0 && (
                          <div className="variant-options">
                            {product.variantType === 'color' ? 'Couleurs' : 
                             product.variantType === 'size' ? 'Tailles' : 
                             product.variantType === 'material' ? 'Matériaux' : 
                             product.variantType === 'style' ? 'Styles' : 'Options'}: {product.variants.length}
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </main>
        </div>
      </div>
    </ShopLayout>
  );
} 